import { useCallback, useContext, useState } from "react";

import toast from "react-hot-toast";
import { withTranslation } from "react-i18next";
import { connect } from "react-redux";
import { Link, useNavigate } from "react-router-dom";

import { formatDistanceToNow } from "date-fns";
import {
  Button,
  Container,
  Dropdown,
  Grid,
  Icon,
  Popup,
  Transition,
} from "semantic-ui-react";
import styled, { ThemeContext } from "styled-components";

import {
  LabelChooser,
  LabelItem,
} from "simplydo/react/components/Choosers/LabelChooser/LabelChooser";

import actions from "actions";
import api from "api";
import util from "utils/utils";

import {
  AnonymousChip,
  IdeaBusinessProfileChip,
  UserChip,
} from "components/lib/Chips";
import IdeaPdfExport from "components/lib/IdeaPdfExport";
import { IdeaLikeHandler } from "components/lib/Ideas";
import PinIdeaHelper from "components/lib/Ideas/PinIdeaHelper";
import { Banner, PlaceholderItem, PlaceholderStack } from "components/lib/UI";

import IdeaTags from "./Tags";

const OptionsMenu = styled(Dropdown.Menu)`
  width: 300px;
  min-width: 0 !important;
  .labels {
    display: flex;
    flex-wrap: wrap;
    .label {
      width: 50%;
    }
    .label:hover {
      background-color: #e9ebee95;
      cursor: pointer;
    }
  }
`;

const StyledLaneInfo = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  line-height: 1;
  color: white;
  > i.icon {
  }
  > div {
    display: flex;
    flex-direction: column;
    > span {
      display: block;
      margin: 0;
    }
    .title-info {
      font-size: 0.8em;
    }
    .lane-name {
      font-weight: bold;
      font-size: 1em;
      color: white;
    }
  }
`;

export const LaneInfo = ({ board, idea, style }) => {
  if (idea?._id && board?.laneObject?.name) {
    if (board.laneObject?.laneCount === 0) return null;
    return (
      <Popup
        disabled={!board.movedAt}
        trigger={
          <StyledLaneInfo style={style}>
            <Icon name="compass" />
            <div>
              <span className="title-info">PROJECT LANE</span>
              <Link
                className="lane-name"
                to={`/challenges/${idea.challenge}/ideas?queryView=board&idea=${idea._id}`}
              >
                {board.laneObject.name}
              </Link>
            </div>
          </StyledLaneInfo>
        }
        content={`${board.movedAt ? `Moved ${formatDistanceToNow(new Date(board.movedAt), { addSuffix: true })}` : ""} ${board.movedBy ? ` by ${board.movedBy?.profile?.fullName}` : ""}`}
      />
    );
  }
  return null;
};

const IdeaHeader = ({
  idea = {},
  loading,
  isEditing,
  updateIdea,
  updateFilters,
  user,
  t,
  deleteIdea,
  onFollow,
  leaveIdea,
}) => {
  const theme = useContext(ThemeContext);
  const [labelChooserOpen, setLabelChooserOpen] = useState(false);
  const ideaId = idea?._id;
  const userId = user?._id;
  const ideaChallenge = idea?.challenge;
  const canManageChallenge = util.canManageChallenge(user, idea.ownerChallenge);

  const navigate = useNavigate();

  const joinIdea = useCallback(() => {
    api.ideas.addCollaborator(
      ideaId,
      userId,
      ({ authors, collaborators }) => {
        updateIdea(authors, ["authors"], true);
        updateIdea(collaborators, ["collaborators"], true);
      },
      (err) => toast.error(err.message),
    );
  }, [ideaId, userId, updateIdea]);

  const findIdeasWithTag = useCallback(
    (value) => {
      updateFilters({ ideaFilter: value });
    },
    [updateFilters],
  );

  const voteForIdea = useCallback(
    (addVote) => {
      api.ideas.vote(
        ideaId,
        addVote,
        ({ addedVotesFor, removedVotesFor }) => {
          const newIdea = {
            votedFor: idea.votedFor,
            voteCount: idea.voteCount || 0,
          };

          if (!newIdea.voteCount) newIdea.voteCount = 0;
          if (addedVotesFor.indexOf(ideaId) > -1) {
            onFollow("challenge", ideaChallenge);
            newIdea.votedFor = true;
            newIdea.voteCount += 1;
          }
          if (removedVotesFor.indexOf(ideaId) > -1) {
            newIdea.votedFor = false;
            newIdea.voteCount -= 1;
          }
          updateIdea(newIdea.voteCount, ["voteCount"], true);
          updateIdea(newIdea.votedFor, ["votedFor"], true);
        },
        (err) => {
          toast.error(err.message);
        },
      );
    },
    [
      ideaId,
      updateIdea,
      ideaChallenge,
      idea.votedFor,
      idea.voteCount,
      onFollow,
    ],
  );

  const pinIdea = useCallback(() => {
    api.ideas.updateStatus(
      ideaId,
      { isPinned: !idea.isPinned },
      (ideaStatus) => {
        updateIdea(ideaStatus.isPinned, ["isPinned"], true);
      },
      (err) => toast.error(err.message),
    );
  }, [ideaId, idea.isPinned, updateIdea]);

  const labelIdea = useCallback(
    (label, isApplied) => {
      let labels = idea.labels || [];
      if (labels.includes(label)) {
        if (!isApplied) {
          labels = labels.filter((l) => l !== label);
        }
      } else {
        if (isApplied) {
          labels = [...labels, label];
        }
      }

      api.ideas.updateStatus(ideaId, { labels }, () => {
        updateIdea(labels, ["labels"], true);
      });
    },
    [ideaId, idea.labels, updateIdea],
  );

  const createChallengeFromIdea = useCallback(() => {
    api.challenges.createFromIdea(
      { idea: ideaId },
      (data) => {
        navigate(`/challenges/${data.challenge._id}`);
        toast.success("Challenge created");
      },
      (err) => toast.error(err.message),
    );
  }, [ideaId, navigate]);

  if (isEditing) return null;
  const board = idea.projectManagement?.boards?.filter(
    (b) => b.forId === idea.challenge,
  )[0];
  return (
    <Transition visible={!isEditing} animation="slide down" duration={400}>
      <Banner
        marginless
        image={util.mixinCssUrlFallback(
          util.ideaCoverImage(idea),
          util.ideaCoverImage(),
        )}
        style={{ zIndex: 200 }}
      >
        <LabelChooser
          isOpen={labelChooserOpen}
          onClose={() => setLabelChooserOpen(false)}
          onComplete={(em) => labelIdea(em, true)}
          existing={idea.labels}
          onRemoveExisting={(em) => labelIdea(em, false)}
        />
        <div className="overlay" />
        <div className="overlay-body">
          <Container>
            <Grid stackable>
              <Grid.Column computer={16}>
                {loading ?
                  <PlaceholderStack
                    gap={10}
                    style={{ marginBottom: 30, marginTop: 30 }}
                  >
                    <PlaceholderItem height={30} />
                    <PlaceholderItem height={10} />
                  </PlaceholderStack>
                : <>
                    <h1 style={{ margin: "0 0 10px 0" }}>
                      {idea.name ||
                        `${t("common:capitalise", { key: "generic.ideas" })} ${idea.ownerChallenge?.name ? `for ${idea.ownerChallenge?.name}` : ""}`}
                    </h1>

                    <h5
                      style={{
                        fontSize: 14,
                        marginTop: 0,
                        marginBottom: board?.laneObject?.name ? 10 : 0,
                      }}
                    >
                      <span style={{ textTransform: "uppercase" }}>
                        {t("generic.idea")}
                      </span>
                      {idea.ownerChallenge && (
                        <span style={{ color: "white" }}>
                          {" "}
                          For{" "}
                          <Link
                            style={{ color: "white" }}
                            to={`/challenges/${idea.challenge}`}
                          >
                            {idea.ownerChallenge?.name}
                          </Link>
                        </span>
                      )}
                    </h5>
                    {canManageChallenge && (
                      <LaneInfo
                        board={board}
                        idea={idea}
                        style={{ marginBottom: 20 }}
                      />
                    )}

                    <IdeaTags
                      idea={idea}
                      findIdeasWithTag={findIdeasWithTag}
                      updateIdea={updateIdea}
                      isMobile={theme.sizes.isMobile}
                    />
                    <div>
                      {idea.labels?.map((label) => (
                        <LabelItem key={label} label={label} />
                      ))}
                      {(
                        util.hasPermission(
                          user,
                          "challenge.editIdeaLabels",
                          idea.challenge,
                        )
                      ) ?
                        <Button
                          size="mini"
                          compact
                          onClick={() => setLabelChooserOpen(true)}
                          style={{
                            marginLeft: 8,
                            fontSize: 10,
                            padding: "0.2em 0.6em",
                          }}
                        >
                          <Icon name="bookmark" /> Edit Labels
                        </Button>
                      : null}
                    </div>
                  </>
                }
              </Grid.Column>
              <Grid.Column
                computer={12}
                textAlign="left"
                verticalAlign="bottom"
              >
                {loading ?
                  <PlaceholderItem height={15} style={{ width: "50%" }} />
                : <div style={{ color: "white" }}>
                    {"Created by "}
                    {(!idea.authors || idea.authors.length === 0) && (
                      <AnonymousChip />
                    )}
                    {idea.lockedOwnerIdeaBusinessProfile ?
                      <IdeaBusinessProfileChip
                        inverted
                        ideaBusinessProfile={
                          idea.lockedOwnerIdeaBusinessProfile
                        }
                      />
                    : null}
                    {(idea.authors || []).map((a) => (
                      <UserChip
                        key={a._id}
                        inverted
                        style={{ margin: 5 }}
                        user={a}
                      />
                    ))}
                  </div>
                }
              </Grid.Column>
              <Grid.Column
                computer={4}
                textAlign="right"
                verticalAlign="bottom"
              >
                <>
                  {(
                    idea.ownerChallenge &&
                    idea.ownerChallenge.votingVisibility === "users"
                  ) ?
                    <IdeaLikeHandler
                      idea={idea}
                      challenge={idea.ownerChallenge}
                      onLikeIdea={(_ideaId, addVote) => voteForIdea(addVote)}
                      trigger={
                        <Button
                          basic={!idea.votedFor}
                          primary={idea.votedFor}
                          icon="thumbs up"
                          style={{ marginLeft: 5 }}
                          size="tiny"
                          label={
                            (
                              idea.ownerChallenge.voteCountVisibility ===
                              "users"
                            ) ?
                              {
                                basic: true,
                                pointing: "left",
                                content: idea.voteCount || 0,
                              }
                            : null
                          }
                        />
                      }
                    />
                  : null}

                  {(
                    util.canManageChallenge(user, idea.ownerChallenge) ||
                    util.hasPermission(
                      user,
                      "challenge.pinIdeas",
                      idea.challenge,
                    )
                  ) ?
                    <Popup
                      content={
                        <div>
                          <p>
                            <b>
                              {idea.isPinned ?
                                t("ideas.options.pin.unPin")
                              : t("ideas.options.pin.challenge")}
                            </b>
                          </p>

                          <PinIdeaHelper challenge={idea.ownerChallenge} />
                        </div>
                      }
                      trigger={
                        <Button
                          size="small"
                          primary={idea.isPinned}
                          basic={!idea.isPinned}
                          icon="thumbtack"
                          onClick={pinIdea}
                        />
                      }
                    />
                  : null}

                  {!loading ?
                    <IdeaPdfExport
                      trigger={<Button size="small" icon="file pdf" basic />}
                      ideas={[idea]}
                      challenge={idea.ownerChallenge}
                    />
                  : null}

                  {(
                    util.canManageChallenge(user, idea.ownerChallenge) ||
                    util.hasPermission(
                      user,
                      "challenge.moveIdeaToChallenge",
                      idea.challenge,
                    ) ||
                    util.hasPermission(
                      user,
                      "challenge.editIdeaLabels",
                      idea.challenge,
                    ) ||
                    util.hasPermission(
                      user,
                      "org.managePermissions",
                      user.ownerOrganisation._id,
                    )
                  ) ?
                    <Dropdown
                      icon={null}
                      direction="left"
                      trigger={
                        <Button
                          basic
                          size="small"
                          icon="chevron down"
                          data-testid="idea-options-dropdown"
                        />
                      }
                    >
                      <OptionsMenu>
                        {idea.user !== user._id && (
                          <Dropdown.Item
                            icon={`user ${util.canEditIdea(user, idea) ? "times" : "plus"}`}
                            content={
                              util.canEditIdea(user, idea) ?
                                `Leave ${t("generic.idea")}`
                              : `Collaborate on this ${t("generic.idea")}`
                            }
                            onClick={
                              util.canEditIdea(user, idea) ? leaveIdea : (
                                joinIdea
                              )
                            }
                          />
                        )}
                        {(
                          util.hasPermission(
                            user,
                            "challenge.moveIdeaToChallenge",
                            idea.challenge,
                          ) && idea.challengeCreation
                        ) ?
                          <Dropdown.Item
                            icon="target"
                            onClick={() => createChallengeFromIdea()}
                          >
                            Create {t("generic.challengeWithArticle")} from this{" "}
                            {t("generic.idea")}
                          </Dropdown.Item>
                        : null}

                        <Dropdown.Divider />

                        {(
                          !idea?.ownerChallenge?.preventIdeaCoverImages &&
                          idea?.coverImage
                        ) ?
                          <Dropdown.Item
                            as="a"
                            href={idea.coverImageUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            content="Download cover image"
                            icon="download"
                          />
                        : null}

                        {(
                          util.hasPermission(
                            user,
                            "challenge.editIdeaLabels",
                            idea.challenge,
                          ) && labelIdea
                        ) ?
                          <Dropdown.Item
                            icon="bookmark"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setLabelChooserOpen(true);
                            }}
                            content="Apply a label"
                          />
                        : null}

                        {(
                          util.canEditIdea(user, idea) ||
                          util.hasPermission(
                            user,
                            "org.managePermissions",
                            user.ownerOrganisation._id,
                          )
                        ) ?
                          <>
                            <Dropdown.Divider />
                            <Dropdown.Item
                              icon="trash"
                              content={`Delete ${t("generic.idea")}`}
                              onClick={() => deleteIdea(true)}
                            />
                          </>
                        : null}
                      </OptionsMenu>
                    </Dropdown>
                  : null}
                </>
              </Grid.Column>
            </Grid>
          </Container>
        </div>
      </Banner>
    </Transition>
  );
};

const mapStateToProps = (state) => ({
  user: state.user,
  challenge: state.challenge,
});

const mapDispatchToProps = (dispatch) => ({
  updateFilters: (data) => dispatch(actions.challenges.updateIdeaFilters(data)),
  onFollow: (context, id) => dispatch(actions.user.onFollow(context, id)),
});

export default withTranslation()(
  connect(mapStateToProps, mapDispatchToProps)(IdeaHeader),
);
